// Presentation-status registries for the split POI / Find & Fix leak entity model.
//
// One platform.poi row can project two map entities: every root row is a POI entity, and a
// leak entity is derived for leak-found roots (the first confirmed leak lives on the root
// row itself) while child rows are leak entities in their own right. Each family carries
// its own status vocabulary, derived server-side; the derivation functions here exist to
// hold frontend fixtures to the same truth table as the backend's SQL (see
// PoiEntityStatusSqlTests — the rows are kept textually identical in both suites).
//
// 'unclassified' is a deliberate, visible status in both families: any stored combination
// the derivation does not explicitly name (contradictory fixed flags on a non-confirmed
// assessment, child rows still at Awaiting Assessment, out-of-range values) surfaces as
// Unclassified on every surface so the underlying data gets found and fixed, instead of
// being silently mapped to a plausible status.

export type EntityType = 'poi' | 'leak';

export type PoiEntityStatus =
    | 'awaiting_assessment'
    | 'leak_found'
    | 'no_leak_found'
    | 'inconclusive'
    | 'unable_to_check'
    | 'unclassified';

export type LeakEntityStatus =
    | 'leak_found'
    | 'already_marked_up'
    | 'no_leak_found'
    | 'inconclusive'
    | 'unable_to_check'
    | 'repaired'
    | 'no_dig_repaired'
    | 'unsuitable'
    | 'rechecked'
    | 'initial_repair_failed'
    | 'recheck_failed'
    | 'unclassified';

export interface EntityStatusDefinition<S extends string> {
    status: S;
    /**
     * Disabled statuses are typed and registered but must render nowhere: no filter
     * switch, no marker image, no icon-expression branch. They exist so activation later
     * is a data change (flip to true) rather than a code hunt, and so "disabled statuses
     * render nowhere" is runtime-testable.
     */
    enabled: boolean;
    label: string;
    /** Pale disc fill from the marker design pack. */
    discColor: string;
    /** The status's saturated glyph colour from the marker design pack. */
    glyphColor: string;
    /** Selected-state halo tint (the status accent; where the glyph is near-white the family accent). */
    haloColor: string;
}

export interface LeakStatusDefinition extends EntityStatusDefinition<LeakEntityStatus> {
    /** Badge variants use the taller 52×80 marker base with the satellite badge circle. */
    badged: boolean;
}

// Designed Unclassified colours: pale purple disc with the saturated purple glyph —
// deliberately colliding with nothing in either family's domain palette, so "needs data
// attention" always reads as its own thing. The leak variant uses the badged base with a
// dash badge (the designed treatment); the POI variant renders the dash glyph on the
// standard base in the same palette.
const UNCLASSIFIED_COLOURS = { discColor: '#E0C4F4', glyphColor: '#8E4EC6', haloColor: '#8E4EC6' };

export const POI_ENTITY_STATUSES: readonly EntityStatusDefinition<PoiEntityStatus>[] = [
    {
        status: 'awaiting_assessment',
        enabled: true,
        label: 'Awaiting Assessment',
        discColor: '#D9D9D9',
        glyphColor: '#FFFFFF',
        haloColor: '#D9D9D9',
    },
    {
        status: 'leak_found',
        enabled: true,
        label: 'Asmt. Complete – Leak Found',
        discColor: '#ACD8FC',
        glyphColor: '#0090FF',
        haloColor: '#0090FF',
    },
    {
        status: 'no_leak_found',
        enabled: true,
        label: 'Asmt. Complete – No Leak Found',
        discColor: '#FDBDBE',
        glyphColor: '#E5484D',
        haloColor: '#E5484D',
    },
    {
        status: 'inconclusive',
        enabled: true,
        label: 'Inconclusive',
        discColor: '#FBE577',
        glyphColor: '#E2A336',
        haloColor: '#E2A336',
    },
    {
        status: 'unable_to_check',
        enabled: true,
        label: 'Unable To Check',
        discColor: '#FFC182',
        glyphColor: '#F76B15',
        haloColor: '#F76B15',
    },
    { status: 'unclassified', enabled: true, label: 'Unclassified', ...UNCLASSIFIED_COLOURS },
];

export const LEAK_ENTITY_STATUSES: readonly LeakStatusDefinition[] = [
    // The Leak Found droplet is near-white on the pale blue disc, so its halo uses the
    // family's saturated blue rather than the glyph colour.
    {
        status: 'leak_found',
        enabled: true,
        label: 'Leak Found',
        discColor: '#ACD8FC',
        glyphColor: '#FBFDFF',
        haloColor: '#0090FF',
        badged: false,
    },
    {
        status: 'already_marked_up',
        enabled: true,
        label: 'Leak Already Marked Up',
        discColor: '#ACD8FC',
        glyphColor: '#0090FF',
        haloColor: '#0090FF',
        badged: false,
    },
    // An additional leak whose assessment concluded no leak was present — the routine
    // "checked, nothing there" survey outcome. A real presentation status, but never
    // counted in the Leaks Found figures (the backend's countable-leak rule).
    {
        status: 'no_leak_found',
        enabled: true,
        label: 'No Leak Found',
        discColor: '#FDBDBE',
        glyphColor: '#E5484D',
        haloColor: '#E5484D',
        badged: false,
    },
    {
        status: 'inconclusive',
        enabled: true,
        label: 'Inconclusive',
        discColor: '#FBE577',
        glyphColor: '#E2A336',
        haloColor: '#E2A336',
        badged: true,
    },
    {
        status: 'unable_to_check',
        enabled: true,
        label: 'Unable To Check',
        discColor: '#FFC182',
        glyphColor: '#F76B15',
        haloColor: '#F76B15',
        badged: true,
    },
    // Repaired and No Dig® Repaired intentionally share one visual; labels, filters and
    // tooltips carry the distinction.
    {
        status: 'repaired',
        enabled: true,
        label: 'Repaired',
        discColor: '#ACDEC8',
        glyphColor: '#29A383',
        haloColor: '#29A383',
        badged: false,
    },
    {
        status: 'no_dig_repaired',
        enabled: true,
        label: 'No Dig® Repaired',
        discColor: '#ACDEC8',
        glyphColor: '#29A383',
        haloColor: '#29A383',
        badged: false,
    },
    { status: 'unclassified', enabled: true, label: 'Unclassified', ...UNCLASSIFIED_COLOURS, badged: true },
    // Disabled: the Unsuitable data rule is unconfirmed — no derivation exists to feed it.
    {
        status: 'unsuitable',
        enabled: false,
        label: 'Unsuitable',
        discColor: '#FFC182',
        glyphColor: '#F76B15',
        haloColor: '#F76B15',
        badged: false,
    },
    // Disabled: the recheck/repair-failure lifecycle has no data source yet. Initial Repair
    // Failed and Recheck Failed intentionally share one visual, like the repaired pair.
    {
        status: 'rechecked',
        enabled: false,
        label: 'Rechecked',
        discColor: '#ACDEC8',
        glyphColor: '#29A383',
        haloColor: '#29A383',
        badged: true,
    },
    {
        status: 'initial_repair_failed',
        enabled: false,
        label: 'Initial Repair Failed',
        discColor: '#FDBDBE',
        glyphColor: '#E5484D',
        haloColor: '#E5484D',
        badged: true,
    },
    {
        status: 'recheck_failed',
        enabled: false,
        label: 'Recheck Failed',
        discColor: '#FDBDBE',
        glyphColor: '#E5484D',
        haloColor: '#E5484D',
        badged: true,
    },
];

export function enabledPoiStatuses(): EntityStatusDefinition<PoiEntityStatus>[] {
    return POI_ENTITY_STATUSES.filter((d) => d.enabled);
}

export function enabledLeakStatuses(): LeakStatusDefinition[] {
    return LEAK_ENTITY_STATUSES.filter((d) => d.enabled);
}

const POI_STATUS_BY_KEY = new Map(POI_ENTITY_STATUSES.map((d) => [d.status, d]));
const LEAK_STATUS_BY_KEY = new Map(LEAK_ENTITY_STATUSES.map((d) => [d.status, d]));

// Both registries define 'unclassified', so these lookups cannot miss; the runtime throw
// exists only to keep the fallback honest without a non-null assertion.
function requireDefinition<T>(definition: T | undefined): T {
    if (definition === undefined) throw new Error('entity status registry is missing its unclassified entry');
    return definition;
}

export function getPoiEntityStatusDefinition(status: PoiEntityStatus): EntityStatusDefinition<PoiEntityStatus> {
    return POI_STATUS_BY_KEY.get(status) ?? requireDefinition(POI_STATUS_BY_KEY.get('unclassified'));
}

export function getLeakEntityStatusDefinition(status: LeakEntityStatus): LeakStatusDefinition {
    return LEAK_STATUS_BY_KEY.get(status) ?? requireDefinition(LEAK_STATUS_BY_KEY.get('unclassified'));
}

/** The wire tolerance rule: a feature without an entity_type is a POI entity. */
export function normaliseEntityType(raw: string | null | undefined): EntityType {
    return raw === 'leak' ? 'leak' : 'poi';
}

/**
 * Frontend mirror of the backend's poi_status derivation — used only to hold fixtures to
 * the backend truth table; production statuses always come off the wire.
 */
export function derivePoiEntityStatus(leakageResult: number | null | undefined): PoiEntityStatus {
    switch (leakageResult) {
        case 1:
        case 4:
            return 'leak_found';
        case 0:
            return 'awaiting_assessment';
        case 2:
            return 'no_leak_found';
        case 3:
            return 'inconclusive';
        case 5:
            return 'unable_to_check';
        default:
            return 'unclassified';
    }
}

/**
 * Frontend mirror of the backend's leak_status derivation. A repaired state requires a
 * confirmed leak (1/4) AND fixed = true — nodig_fix alone never repairs, and fixed on any
 * non-confirmed assessment (No Leak Found, Inconclusive, Unable To Check) is contradictory
 * data, not a repair. No Leak Found only ever applies to child rows — roots at 2 project
 * no leak entity.
 */
export function deriveLeakEntityStatus(
    leakageResult: number | null | undefined,
    fixed: boolean | null | undefined,
    nodigFix: boolean | null | undefined,
): LeakEntityStatus {
    const confirmed = leakageResult === 1 || leakageResult === 4;
    if (confirmed && fixed === true && nodigFix === true) return 'no_dig_repaired';
    if (confirmed && fixed === true) return 'repaired';
    if (leakageResult === 4 && fixed !== true) return 'already_marked_up';
    if (leakageResult === 1 && fixed !== true) return 'leak_found';
    if (leakageResult === 2 && fixed !== true) return 'no_leak_found';
    if (leakageResult === 3 && fixed !== true) return 'inconclusive';
    if (leakageResult === 5 && fixed !== true) return 'unable_to_check';
    return 'unclassified';
}

/**
 * Frontend mirror of the leak-population rule: every child row is a leak entity (a child
 * is by definition a recorded leak — anomalies present as Unclassified), plus roots with a
 * confirmed leak, whose first leak lives on the root row itself.
 */
export function isLeakEntityRow(hasParent: boolean, leakageResult: number | null | undefined): boolean {
    return hasParent || leakageResult === 1 || leakageResult === 4;
}
